import type { DatabaseStatus, DeploymentStatus, ServiceStatus } from '../lib/types'
import { Tooltip } from './overlay'
import { Pill, type Tone } from './ui'

type Status = ServiceStatus | DeploymentStatus | DatabaseStatus

/** Every status maps onto one of five visual states. */
const TONE: Record<Status, Tone> = {
  new: 'idle',
  live: 'ok',
  available: 'ok',
  building: 'busy',
  queued: 'busy',
  cloning: 'busy',
  pushing: 'busy',
  deploying: 'busy',
  creating: 'busy',
  failed: 'bad',
  canceled: 'idle',
  superseded: 'idle',
  suspended: 'warn',
  deleting: 'busy',
  deleted: 'idle',
}

const LABEL: Partial<Record<Status, string>> = {
  new: 'Not deployed',
  live: 'Live',
  available: 'Available',
  building: 'Building',
  queued: 'Queued',
  cloning: 'Cloning',
  pushing: 'Pushing image',
  deploying: 'Deploying',
  creating: 'Provisioning',
  failed: 'Failed',
  canceled: 'Canceled',
  superseded: 'Superseded',
  suspended: 'Suspended',
  deleted: 'Deleted',
}

/** What each state actually means. Shown on hover and on focus, because
 *  "superseded" and "canceled" are the two nobody guesses correctly. */
const MEANING: Partial<Record<Status, string>> = {
  new: 'Created, but never deployed. Push to the tracked branch or deploy by hand.',
  live: 'Serving traffic.',
  available: 'Provisioned and accepting connections from your services.',
  building: 'An image is being built from your commit.',
  queued: 'Waiting for a builder to pick this up.',
  cloning: 'Fetching your commit from GitHub.',
  pushing: 'Storing the built image in the registry in Addis Ababa.',
  deploying: 'Rolling out and waiting for the health check to pass.',
  creating: 'Pulling the image and initialising storage.',
  failed: 'The last attempt did not finish. The build log says why.',
  canceled: 'Stopped on request before it finished.',
  superseded: 'A newer deploy took over before this one went live.',
  suspended: 'Stopped and not billing. Its data and disks are kept.',
  deleted: 'Removed. Kept in the audit trail only.',
}

/** The colour a card's edge stripe takes, so health reads before any text. */
export const STRIPE: Record<Tone, string> = {
  ok: 'var(--ok)',
  busy: 'var(--info)',
  warn: 'var(--warn)',
  bad: 'var(--danger)',
  idle: 'var(--border-strong)',
}

export function toneFor(status: string): Tone {
  return TONE[status as Status] ?? 'idle'
}

export default function StatusBadge({ status }: { status: string }) {
  const pill = <Pill tone={toneFor(status)}>{LABEL[status as Status] ?? status.replaceAll('_', ' ')}</Pill>
  const meaning = MEANING[status as Status]
  return meaning ? <Tooltip label={meaning}>{pill}</Tooltip> : pill
}

/** True while a deploy is still moving, so callers know to keep polling. */
export function isInFlight(status: DeploymentStatus): boolean {
  return ['queued', 'cloning', 'building', 'pushing', 'deploying'].includes(status)
}


